import React from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Home,
  Phone,
  Users,
  List,
  Clock,
  Timer,
  MessageSquare,
  TrendingUp,
  Brain,
  ClipboardCheck,
  Gauge,
  Target,
  UserCheck,
  BookOpen,
  Hotel,
  LogOut,
  CheckSquare,
  FileBarChart,
  User as UserIcon,
  KeyRound,
  ShieldCheck,
  GraduationCap
} from 'lucide-react';
import { getStoredUser, logoutApi } from '../api';
import { CONOCIMIENTO_EN_VIVO, tieneBandera, useBanderas, useBanderasDeAgente } from '../banderas';

// ─── Admin sidebar ──────────────────────────────────────────────────────────
const ADMIN_MAIN_LINKS = [
  { to: '/', label: 'Overview', Icon: Home },
  { to: '/calls', label: 'Llamadas', Icon: Phone },
  { to: '/agents', label: 'Agentes', Icon: Users },
  { to: '/queues', label: 'Colas', Icon: List },
  { to: '/customers', label: 'Clientes', Icon: UserCheck },
];

const ADMIN_ANALYTICS_LINKS = [
  { to: '/analytics/temporal', label: 'Temporal', Icon: Clock },
  { to: '/analytics/duration', label: 'Duración', Icon: Timer },
  { to: '/analytics/conversation', label: 'Conversación', Icon: MessageSquare },
  { to: '/analytics/forecast', label: 'Pronóstico', Icon: TrendingUp },
  { to: '/analytics/analysis', label: 'Análisis IA', Icon: Brain },
  { to: '/analytics/compliance', label: 'Cumplimiento', Icon: ClipboardCheck },
  { to: '/analytics/usefulness', label: 'Utilidad', Icon: CheckSquare },
  { to: '/conversion', label: 'Conversión', Icon: Target },
  { to: '/library/chat', label: 'Chat biblioteca', Icon: BookOpen },
];

const ADMIN_MARKETING_LINKS = [
  { to: '/reports/sales', label: 'Reporte ventas', Icon: FileBarChart },
];

// Enlaces que dependen de una bandera de funcionalidad (#109).
//
// Se declaran aparte y no dentro de las listas fijas: la condición vive en UN
// lugar, y un enlace que se pinta o no según el servidor no debe parecer
// permanente al leer la lista.
// `preview` es el tramo final de la ruta de "Ver como agente". Se declara junto
// al enlace y no se deriva de `to` con un replace: la previsualización vive en
// `/agents/:name/ver/...`, que no es `/agent/...` con un prefijo distinto, y
// una derivación por texto se rompe callada en cuanto una ruta no siga el
// patrón.
const ENLACES_CON_BANDERA = {
  admin: { [CONOCIMIENTO_EN_VIVO]: { to: '/conocimiento', label: 'Conocimiento', Icon: Hotel } },
  agent: {
    [CONOCIMIENTO_EN_VIVO]: {
      to: '/agent/conocimiento', preview: 'conocimiento',
      label: 'Conocimiento', Icon: Hotel,
    },
  },
};

const ADMIN_ADMIN_LINKS = [
  { to: '/coaching', label: 'Coaching', Icon: GraduationCap },
  // La cobertura va junto al registro y no en Analítica: es el mismo tema, y
  // buscarla entre las gráficas del negocio sería buscarla donde no está.
  { to: '/coaching/cobertura', label: 'Cobertura', Icon: Gauge },
  { to: '/users', label: 'Usuarios', Icon: ShieldCheck },
];

// ─── Agent sidebar ──────────────────────────────────────────────────────────
const AGENT_LINKS = [
  { to: '/agent', label: 'Inicio', Icon: Home },
  { to: '/agent/llamadas', label: 'Mis llamadas', Icon: Phone },
  { to: '/agent/me', label: 'Mi ficha', Icon: UserIcon },
  { to: '/agent/customers', label: 'Mis clientes', Icon: UserCheck },
  { to: '/agent/compliance', label: 'Mi cumplimiento', Icon: ClipboardCheck },
  { to: '/agent/coaching', label: 'Mi coaching', Icon: GraduationCap },
  { to: '/agent/conversion', label: 'Mi conversión', Icon: Target },
  { to: '/agent/library/chat', label: 'Chat biblioteca', Icon: BookOpen },
];

function NavItem({ to, label, Icon, end }) {
  return (
    <NavLink
      to={to}
      end={end ?? (to === '/' || to === '/agent')}
      className={({ isActive }) => `sidebar-link${isActive ? ' active' : ''}`}
    >
      <Icon size={16} />
      {label}
    </NavLink>
  );
}

/**
 * Layout shell con sidebar de navegación.
 *
 * @param {'admin' | 'agent'} variant — admin (default): muestra todas las
 *   secciones. 'agent': muestra sólo las pestañas pertinentes al agente,
 *   todas con scope automático a sus propias llamadas (vía backend).
 */
export default function Layout({ children, variant = 'admin', previewAgent = null }) {
  const navigate = useNavigate();
  const user = getStoredUser();

  // Modo "ver como agente" (admin): el sidebar del agente apunta a las rutas de
  // preview /agents/:name/ver/* (datos acotados a ese agente, solo lectura).
  const previewBase = previewAgent ? `/agents/${encodeURIComponent(previewAgent)}/ver` : null;
  // El MISMO menú que ve el agente, con las rutas de previsualización. Que sean
  // dos listas distintas es lo que dejó fuera "Mi coaching" desde que se creó:
  // se agregó a `AGENT_LINKS` y nadie lo agregó aquí, y el modo de falla es una
  // pestaña que no está — no hay error, no hay nada roto, sólo falta.
  //
  // Por eso hay una prueba que compara las dos listas etiqueta por etiqueta: es
  // lo único que impide que la próxima pestaña se quede fuera igual.
  const PREVIEW_LINKS = previewAgent ? [
    { to: previewBase, label: 'Inicio', Icon: Home, end: true },
    { to: `${previewBase}/llamadas`, label: 'Mis llamadas', Icon: Phone },
    { to: `${previewBase}/me`, label: 'Mi ficha', Icon: UserIcon },
    { to: `${previewBase}/clientes`, label: 'Mis clientes', Icon: UserCheck },
    { to: `${previewBase}/cumplimiento`, label: 'Mi cumplimiento', Icon: ClipboardCheck },
    { to: `${previewBase}/coaching`, label: 'Mi coaching', Icon: GraduationCap },
    { to: `${previewBase}/conversion`, label: 'Mi conversión', Icon: Target },
    { to: `${previewBase}/biblioteca`, label: 'Chat biblioteca', Icon: BookOpen },
  ] : [];

  const isAgent = variant === 'agent';

  // Las banderas se preguntan al servidor en cada carga, no se leen de
  // localStorage: encenderle una a alguien no puede exigir que vuelva a entrar.
  const { banderas } = useBanderas();
  // En previsualización mandan las banderas DEL AGENTE, no las del supervisor
  // que está mirando. Antes se resolvía escondiendo estas pestañas durante la
  // previsualización, que evitaba prometer una pestaña que el agente no tiene
  // a cambio de esconder una que sí — y con Conocimiento abierta a todos, lo
  // segundo pasa siempre.
  const { banderas: banderasDelAgente } = useBanderasDeAgente(previewAgent);
  const banderasQueMandan = previewAgent ? banderasDelAgente : banderas;
  const conBandera = Object.entries(ENLACES_CON_BANDERA[isAgent ? 'agent' : 'admin'] || {})
    .filter(([clave]) => tieneBandera(banderasQueMandan, clave))
    .map(([, enlace]) => (
      previewAgent && enlace.preview
        ? { ...enlace, to: `${previewBase}/${enlace.preview}` }
        : enlace
    ));

  const handleLogout = () => {
    logoutApi();
    navigate('/login', { replace: true });
  };

  const handleChangePassword = () => navigate('/change-password');

  return (
    <div className="app-layout">
      <aside className="sidebar">
        <div className="sidebar-logo">GP<span>Voice</span></div>
        <nav className="sidebar-nav">
          {isAgent ? (
            <>
              {(previewAgent ? PREVIEW_LINKS : AGENT_LINKS).map(l => <NavItem key={l.to} {...l} />)}
              {conBandera.map(l => <NavItem key={l.to} {...l} />)}
            </>
          ) : (
            <>
              {ADMIN_MAIN_LINKS.map(l => <NavItem key={l.to} {...l} />)}
              <div className="nav-divider" />
              <div className="nav-section-label">Analítica</div>
              {ADMIN_ANALYTICS_LINKS.map(l => <NavItem key={l.to} {...l} />)}
              <div className="nav-divider" />
              <div className="nav-section-label">Marketing</div>
              {ADMIN_MARKETING_LINKS.map(l => <NavItem key={l.to} {...l} />)}
              <div className="nav-divider" />
              <div className="nav-section-label">Administración</div>
              {ADMIN_ADMIN_LINKS.map(l => <NavItem key={l.to} {...l} />)}
              {conBandera.map(l => <NavItem key={l.to} {...l} />)}
            </>
          )}
        </nav>
        {user && (
          <div style={{
            marginTop: 'auto',
            padding: '0.75rem 0.9rem',
            borderTop: '1px solid rgba(255,255,255,0.06)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.6rem',
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: '0.75rem', color: '#a0a0a0' }}>
                {previewAgent ? 'Admin · vista agente' : (isAgent ? 'Agente' : 'Sesión')}
              </div>
              <div style={{ fontSize: '0.85rem', color: '#f5f5f0', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {previewAgent ? (user.full_name || user.email) : (isAgent ? (user.agent_name || user.email) : (user.full_name || user.email))}
              </div>
            </div>
            <button
              type="button"
              onClick={handleChangePassword}
              title="Cambiar contraseña"
              aria-label="Cambiar contraseña"
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#a0a0a0',
                padding: '0.4rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <KeyRound size={15} />
            </button>
            <button
              type="button"
              onClick={handleLogout}
              title="Cerrar sesión"
              aria-label="Cerrar sesión"
              style={{
                background: 'transparent',
                border: '1px solid rgba(255,255,255,0.1)',
                color: '#a0a0a0',
                padding: '0.4rem',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <LogOut size={15} />
            </button>
          </div>
        )}
      </aside>
      <main className="main-content">
        {previewAgent && (
          <div style={{ background: 'rgba(129,140,248,0.12)', border: '1px solid #818cf8', borderRadius: 8, padding: '8px 14px', marginBottom: 16, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <span style={{ color: '#c7d2fe', fontSize: 13 }}>
              👁 Vista de agente: <strong>{previewAgent}</strong> — solo lectura (sigues como admin)
            </span>
            <button
              type="button"
              onClick={() => navigate(`/agents/${encodeURIComponent(previewAgent)}`)}
              style={{ background: 'transparent', border: '1px solid #818cf8', color: '#a5b4fc', padding: '4px 12px', borderRadius: 6, cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
            >
              ← Salir de la vista
            </button>
          </div>
        )}
        {children}
      </main>
    </div>
  );
}
